import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  Upload,
  FileText,
  Image as ImageIcon,
  Trash2,
  Loader2,
  Download,
  QrCode,
  ExternalLink,
} from "lucide-react";

import { adminApi, API } from "@/lib/api";
import { LIFECYCLE_STAGES, STAGE_BY_ID } from "@/lib/lifecycle";
import StageTimeline from "@/components/StageTimeline";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

const tierLabel = { basic: "Bronze", standard: "Silver", premium: "Gold" };

export default function AdminSubmission() {
  const { id } = useParams();
  const [submission, setSubmission] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [updating, setUpdating] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [sub, fls] = await Promise.all([
        adminApi.get(`/admin/submissions/${id}`),
        adminApi.get(`/admin/submissions/${id}/files`),
      ]);
      setSubmission(sub.data);
      setFiles(fls.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const changeStatus = async (status) => {
    setUpdating(status);
    try {
      const res = await adminApi.patch(`/admin/submissions/${id}/status`, {
        status,
        note: note.trim() || null,
      });
      setSubmission(res.data);
      setNote("");
      toast.success(`Status updated to ${STAGE_BY_ID[status]?.label}.`);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not update status."
      );
    } finally {
      setUpdating(null);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await adminApi.post(`/admin/submissions/${id}/files`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded.");
      await load();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Upload failed. Try a smaller file."
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onDelete = async (fileId) => {
    try {
      await adminApi.delete(`/admin/files/${fileId}`);
      toast.success("File removed.");
      setFiles((fs) => fs.filter((f) => f.id !== fileId));
    } catch {
      toast.error("Could not remove file.");
    }
  };

  if (loading) {
    return (
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="text-sm text-[#6E6E70]">Loading application…</div>
      </main>
    );
  }

  if (!submission) {
    return (
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-16">
        <div className="text-sm text-[#6E6E70]">Application not found.</div>
        <Link
          to="/admin"
          data-testid="admin-back-to-dashboard"
          className="btn-secondary inline-flex items-center gap-2 mt-6 px-4 py-2 text-sm rounded-none"
        >
          <ArrowLeft size={14} /> Back to dashboard
        </Link>
      </main>
    );
  }

  return (
    <main
      data-testid="admin-submission-page"
      className="max-w-7xl mx-auto px-6 md:px-10 py-10 lg:py-14"
    >
      <Link
        to="/admin"
        data-testid="admin-back-to-dashboard"
        className="inline-flex items-center gap-2 text-sm text-[#6E6E70] hover:text-[#2A1F1A]"
      >
        <ArrowLeft size={14} /> Back to dashboard
      </Link>

      <div className="mt-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div>
          <div className="eyebrow">
            Reference · <span className="font-mono normal-case">{submission.id.slice(0, 8)}</span>
          </div>
          <h1
            data-testid="admin-submission-name"
            className="font-serif-display text-4xl lg:text-5xl mt-3 text-[#2A1F1A]"
          >
            {submission.full_name}
          </h1>
          <div className="mt-2 text-sm text-[#6E6E70]">
            {submission.property_name} · {submission.address}
          </div>
        </div>
        <div className="text-right">
          <div className="eyebrow">Service level</div>
          <div className="font-serif-display text-2xl text-[#2A1F1A] mt-1">
            {tierLabel[submission.tier]}
          </div>
        </div>
      </div>

      {/* Stage timeline */}
      <div className="mt-10 bg-white border border-[#E3DED5] p-6 lg:p-8">
        <div className="eyebrow">Lifecycle</div>
        <div className="mt-5">
          <StageTimeline currentStatus={submission.status} />
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <label className="eyebrow block">
              Internal note for this transition (optional)
            </label>
            <Textarea
              data-testid="admin-status-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Add context — e.g. 'Approved pending interior styling visit'."
              className="mt-2 bg-transparent border border-[#E3DED5] focus-visible:ring-0 focus-visible:border-[#C03028] rounded-none shadow-none p-3 font-sans-body"
            />
          </div>

          <div>
            <label className="eyebrow block">Move to stage</label>
            <div className="mt-2 grid grid-cols-1 gap-2">
              {LIFECYCLE_STAGES.map((s) => {
                const isCurrent = submission.status === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => changeStatus(s.id)}
                    disabled={isCurrent || updating !== null}
                    data-testid={`btn-set-status-${s.id}`}
                    className={cn(
                      "inline-flex items-center justify-between px-4 py-2.5 text-sm border rounded-none transition-colors",
                      isCurrent
                        ? "bg-[#F2EFE9] border-[#E3DED5] text-[#6E6E70] cursor-not-allowed"
                        : "btn-secondary"
                    )}
                  >
                    <span>{s.label}</span>
                    {updating === s.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      isCurrent && (
                        <span className="text-[10px] uppercase tracking-[0.18em]">
                          current
                        </span>
                      )
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* QR + Guest welcome */}
      <QrCard submissionId={submission.id} status={submission.status} />

      {/* Two-column: details + files */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <DetailBlock title="Host">
            <DetailLine k="Name" v={submission.full_name} />
            <DetailLine k="Email" v={submission.email} />
            <DetailLine k="Phone" v={submission.phone || "—"} />
          </DetailBlock>

          <DetailBlock title="Property">
            <DetailLine k="Name" v={submission.property_name} />
            <DetailLine k="Type" v={submission.property_type} />
            <DetailLine k="Address" v={submission.address} />
            <DetailLine
              k="Layout"
              v={`${submission.bedrooms} bed · ${submission.bathrooms} bath · ${submission.max_guests} guests`}
            />
          </DetailBlock>

          <DetailBlock title="Availability & pricing">
            <DetailLine k="Available from" v={submission.available_from || "Flexible"} />
            <DetailLine
              k="Minimum stay"
              v={`${submission.min_stay_nights} night${submission.min_stay_nights > 1 ? "s" : ""}`}
            />
            <DetailLine
              k="Indicative rate"
              v={
                submission.nightly_rate
                  ? `$${submission.nightly_rate} / night`
                  : "To be set together"
              }
            />
          </DetailBlock>

          <DetailBlock title="Amenities">
            {submission.amenities.length === 0 ? (
              <em className="text-sm text-[#6E6E70]">None listed</em>
            ) : (
              <div className="flex flex-wrap gap-2">
                {submission.amenities.map((a) => (
                  <span
                    key={a}
                    className="text-xs border border-[#E3DED5] px-2 py-1"
                  >
                    {a}
                  </span>
                ))}
              </div>
            )}
          </DetailBlock>

          {submission.notes && (
            <DetailBlock title="Host notes">
              <p className="text-sm text-[#6E6E70] leading-relaxed">
                {submission.notes}
              </p>
            </DetailBlock>
          )}

          <DetailBlock title="History">
            {(submission.status_history || []).length === 0 ? (
              <em className="text-sm text-[#6E6E70]">No transitions yet.</em>
            ) : (
              <ol className="space-y-4">
                {[...submission.status_history].reverse().map((h, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-sm"
                    data-testid={`history-entry-${i}`}
                  >
                    <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-none bg-[#B0895C] shrink-0" />
                    <div className="flex-1">
                      <div className="text-[#2A1F1A]">
                        {STAGE_BY_ID[h.status]?.label || h.status}
                      </div>
                      <div className="text-xs text-[#6E6E70]">
                        {new Date(h.at).toLocaleString()} · {h.by}
                      </div>
                      {h.note && (
                        <div className="mt-1 text-sm text-[#6E6E70] italic">
                          “{h.note}”
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </DetailBlock>
        </div>

        {/* Files */}
        <div>
          <div className="bg-white border border-[#E3DED5] p-6 sticky top-24">
            <div className="flex items-center justify-between">
              <div className="eyebrow">Documents</div>
              <span className="text-xs text-[#6E6E70]">{files.length} file(s)</span>
            </div>

            <label
              data-testid="admin-upload-dropzone"
              className={cn(
                "mt-5 block border border-dashed cursor-pointer p-6 text-center transition-colors",
                uploading
                  ? "border-[#C03028] bg-[#F2EFE9]"
                  : "border-[#E3DED5] hover:border-[#C03028] hover:bg-[#F2EFE9]/60"
              )}
            >
              <input
                ref={fileInputRef}
                type="file"
                onChange={onUpload}
                disabled={uploading}
                data-testid="admin-upload-input"
                accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.txt"
                className="hidden"
              />
              {uploading ? (
                <Loader2 className="mx-auto animate-spin text-[#C03028]" size={20} />
              ) : (
                <Upload size={20} className="mx-auto text-[#6E6E70]" />
              )}
              <div className="mt-3 text-sm text-[#2A1F1A]">
                {uploading ? "Uploading…" : "Click to upload a file"}
              </div>
              <div className="mt-1 text-xs text-[#6E6E70]">
                Up to 10MB · jpg, png, pdf, doc, docx
              </div>
            </label>

            <ul className="mt-6 space-y-3" data-testid="admin-file-list">
              {files.length === 0 && (
                <li className="text-sm text-[#6E6E70]" data-testid="admin-files-empty">
                  No documents yet.
                </li>
              )}
              {files.map((f) => (
                <li
                  key={f.id}
                  data-testid={`admin-file-${f.id}`}
                  className="border border-[#E3DED5] p-3 flex items-start gap-3"
                >
                  <FileIcon contentType={f.content_type} />
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-sm text-[#2A1F1A] truncate"
                      title={f.original_filename}
                    >
                      {f.original_filename}
                    </div>
                    <div className="text-xs text-[#6E6E70]">
                      {(f.size / 1024).toFixed(1)} KB ·{" "}
                      <span className="capitalize">{f.uploaded_by}</span>
                      {f.uploader_email && ` · ${f.uploader_email}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <a
                      href={`${API}/files/${f.id}/download`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`admin-file-download-${f.id}`}
                      className="text-[#C03028] hover:text-[#9F2620]"
                      title="Download"
                    >
                      <Download size={16} />
                    </a>
                    <button
                      onClick={() => onDelete(f.id)}
                      data-testid={`admin-file-delete-${f.id}`}
                      className="text-[#6E6E70] hover:text-red-700"
                      title="Remove"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </main>
  );
}

function DetailBlock({ title, children }) {
  return (
    <div className="bg-white border border-[#E3DED5] p-6">
      <div className="eyebrow">{title}</div>
      <div className="mt-4 space-y-2">{children}</div>
    </div>
  );
}

function DetailLine({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <span className="text-[#6E6E70]">{k}</span>
      <span className="text-[#2A1F1A] text-right max-w-[60%] truncate">
        {v || "—"}
      </span>
    </div>
  );
}

function FileIcon({ contentType }) {
  if (contentType?.startsWith("image/")) {
    return <ImageIcon size={18} className="text-[#B0895C] mt-0.5 shrink-0" />;
  }
  return <FileText size={18} className="text-[#B0895C] mt-0.5 shrink-0" />;
}

function QrCard({ submissionId, status }) {
  const [meta, setMeta] = useState(null);
  const [qrBlob, setQrBlob] = useState(null);
  const isLive = status === "onboarding";

  useEffect(() => {
    let revoke;
    (async () => {
      try {
        const m = await adminApi.get(`/admin/submissions/${submissionId}/qr-url`);
        setMeta(m.data);
        const r = await adminApi.get(`/admin/submissions/${submissionId}/qr`, {
          responseType: "blob",
        });
        const url = URL.createObjectURL(r.data);
        revoke = url;
        setQrBlob(url);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [submissionId]);

  return (
    <div
      data-testid="admin-qr-card"
      className="mt-10 border border-[#E3DED5] bg-white p-6 lg:p-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-center"
    >
      <div className="md:col-span-3">
        {qrBlob ? (
          <img
            src={qrBlob}
            alt="Guest QR code"
            data-testid="admin-qr-image"
            className="w-full max-w-[220px] aspect-square border border-[#E3DED5]"
          />
        ) : (
          <div className="w-full aspect-square bg-[#F2EFE9] flex items-center justify-center">
            <QrCode size={36} className="text-[#8E8F90]" />
          </div>
        )}
      </div>
      <div className="md:col-span-6">
        <div className="flex items-center gap-2">
          <QrCode size={14} className="text-[#C03028]" />
          <span className="eyebrow">Guest QR · Janté Travel welcome</span>
        </div>
        <h3 className="font-serif-display text-2xl text-[#2A1F1A] mt-2">
          {isLive ? "Live — share with your host" : "Activates on approval"}
        </h3>
        <p className="mt-2 text-sm text-[#6E6E70] leading-relaxed">
          Each property receives a unique QR code. When guests scan it they land
          on a tailored welcome page introducing Sasha, the host avatar and the
          Janté Travel concierge.
        </p>
        {meta?.guest_url && (
          <a
            href={meta.guest_url}
            target="_blank"
            rel="noreferrer"
            data-testid="admin-qr-guest-link"
            className="mt-4 inline-flex items-center gap-2 text-sm text-[#C03028] hover:text-[#9F2620] underline underline-offset-4"
          >
            <ExternalLink size={14} /> {meta.guest_url}
          </a>
        )}
      </div>
      <div className="md:col-span-3 md:text-right">
        {qrBlob && (
          <a
            href={qrBlob}
            download={`sasha-qr-${submissionId.slice(0, 8)}.png`}
            data-testid="admin-qr-download"
            className="btn-primary inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm tracking-wide rounded-none"
          >
            <Download size={14} /> Download QR
          </a>
        )}
        <div className="mt-3 text-[10px] uppercase tracking-[0.18em] font-semibold">
          {isLive ? (
            <span className="text-[#C03028]">Live</span>
          ) : (
            <span className="text-[#8E8F90]">
              Will go live at &ldquo;Onboarding&rdquo;
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
